import React from "react";
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, useTheme, useMediaQuery } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { motion } from "framer-motion";
import GlassPanel from "./ui/GlassPanel";
import TiltCard from "./ui/TiltCard";

export default function FAQSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: "linear-gradient(180deg, #F8F8FB 0%, #F1F0F6 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          bottom: "-15%",
          left: "-10%",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(253,129,18,0.07) 0%, rgba(253,129,18,0) 70%)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: "space-between",
          maxWidth: "1200px",
          margin: "auto",
          padding: { xs: "0 20px", md: "0 20px" },
          gap: 4
        }}
      >
        {/* Left Side - Text & FAQs */}
        <Box sx={{ flex: 1, maxWidth: "600px" }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "700",
              fontFamily: "Poppins, sans-serif",
              marginBottom: "12px",
              color: "#14141F",
              fontSize: { xs: "1.75rem", md: "2.25rem" },
            }}
          >
            Top Marketing Questions Answered!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#5B5B6E",
              marginBottom: 3,
              fontSize: { xs: "1rem", md: "1.1rem" },
              lineHeight: 1.6
            }}
          >
            In this section, we address frequently asked questions to assist you.
          </Typography>

          {/* FAQ Accordions */}
          {[
            {
              q: "What marketing services does your agency offer?",
              a: "Our agency specializes in digital marketing, brand strategy, and content creation. We tailor our services to fit the unique needs of businesses across various sectors, helping them grow and succeed in a competitive market.",
            },
            {
              q: "How can I contact marketing support?",
              a: (
                <>
                  You can reach our marketing support team by emailing{" "}
                  <strong>slgtdigitalmarketingagency@gmail.com</strong>, calling{" "}
                  <strong>+91 8500501823</strong>, or using the live chat on our website. We're
                  committed to providing prompt and effective marketing solutions.
                </>
              ),
            },
            {
              q: "What is your satisfaction guarantee policy?",
              a: (
                <>
                  We offer a <strong>100% satisfaction guarantee</strong>. If you're not satisfied
                  with our services, we will work closely with you to make things right.
                </>
              ),
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.4, delay: idx * 0.08 }}
            >
              <GlassPanel sx={{ mb: 2, overflow: "hidden" }}>
                <Accordion
                  sx={{
                    background: "transparent",
                    boxShadow: "none",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#FD8112" }} />}>
                    <Typography sx={{ fontWeight: "bold", fontFamily: "Poppins, sans-serif", color: "#14141F" }}>
                      {item.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography sx={{ color: "#5B5B6E", fontFamily: "Nunito, serif" }}>
                      {item.a}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </GlassPanel>
            </motion.div>
          ))}
        </Box>

        {/* Right Side - Lottie Animation */}
        <Box
          sx={{
            flex: 1,
            maxWidth: "500px",
            marginTop: isMobile ? 4 : 0
          }}
        >
          <TiltCard glareColor="#FD8112" maxTilt={7}>
            <GlassPanel
              sx={{
                height: isMobile ? "250px" : "400px",
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 30px 60px rgba(20,20,40,0.1)",
                border: "1px solid rgba(255,255,255,0.8)",
              }}
            >
              <DotLottieReact
                src="https://lottie.host/7360a2e2-2fcd-47f7-b5fe-36d1d42b29a0/vI2obVwChU.lottie"
                loop
                autoplay
                style={{ width: "100%", height: "100%" }}
              />
            </GlassPanel>
          </TiltCard>
        </Box>
      </Box>
    </Box>
  );
}
